"use client"

import { useState, useEffect } from "react"
import { Menu, X, Home, User, Sparkles } from "lucide-react"
import { useLanguage } from "./LanguageContext"
import LanguageSwitcher from "./LanguageSwitcher"
import { usePasswordCheck } from "./hooks/usePasswordCheck"
import "./css/header-nav.css"
import "./css/mobile-menu.css"
import "./css/simple-footer.css"

interface NavigationProps {
  appConfig: any
currentPage: "home" | "about" | "lessons" | "teachers" | "ontdek" | "none"
onNavigate: (page: "home" | "about" | "lessons" | "teachers" | "ontdek") => void

  t: (path: string, defaultText: string) => string
  currentLessonId?: string
  isLessonContext?: boolean
}

const Navigation = ({ appConfig, currentPage, onNavigate, t, currentLessonId, isLessonContext }: NavigationProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { language } = useLanguage()
  const { isLessonUnlocked, isAdmin, signedIn: isSignedIn, username: userName } = usePasswordCheck()

  // The Ontdek link only shows once at least one article is published, or
  // always for an admin (who needs to reach their drafts). Cached in
  // sessionStorage so navigating between pages doesn't flash the link.
  const [hasPublishedArticles, setHasPublishedArticles] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem("techtok-ontdek-has-published") === "1"
    } catch {
      return false
    }
  })

  useEffect(() => {
    let cancelled = false
    fetch("/portal/api/articles-public.php")
      .then((r) => (r.ok && (r.headers.get("content-type") || "").includes("application/json") ? r.json() : null))
      .then((d) => {
        if (cancelled || !d?.success) return
        const has = (d.articles || []).length > 0
        setHasPublishedArticles(has)
        try {
          sessionStorage.setItem("techtok-ontdek-has-published", has ? "1" : "0")
        } catch {
          /* private mode */
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const showOntdek = isAdmin || hasPublishedArticles

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024 && mobileMenuOpen) {
        setMobileMenuOpen(false)
      }
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [mobileMenuOpen])

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }

    return () => {
      document.body.style.overflow = ""
    }
  }, [mobileMenuOpen])

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  const isNavigationBlocked = () => {
    if (!isLessonContext || !currentLessonId) return false
    return !isLessonUnlocked(currentLessonId)
  }

const handleNavigation = (page: "home" | "about" | "lessons" | "teachers" | "ontdek") => {
    if (isNavigationBlocked()) {
      return
    }
    onNavigate(page)
    setMobileMenuOpen(false)
  }

  return (
    <>
      <header className="header">
        <div className="header-content">
          <div className="header-logo">
            <a href="/" className="header-logo-link">
              {appConfig?.branding?.logo && !appConfig.branding.logo.includes("undefined") ? (
                <img
                  src={appConfig.branding.logo || "/placeholder.svg"}
                  alt={appConfig?.branding?.logo || "Logo"}
                  className="header-logo-image"
                />
              ) : (
                <div className="header-text-logo">{appConfig?.branding?.logoAlt || "Techtok"}</div>
              )}
            </a>
          </div>

          <div className="header-nav">
            <a
              href="/"
              className={`nav-link ${currentPage === "home" ? "active" : ""} ${isNavigationBlocked() ? "disabled" : ""}`}
              onClick={(e) => {
                if (isNavigationBlocked()) {
                  e.preventDefault()
                }
              }}
              tabIndex={isNavigationBlocked() ? -1 : 0}
            >
              {t("interface.home", "Home")}
            </a>
            <a
              href="/about"
              className={`nav-link ${currentPage === "about" ? "active" : ""} ${isNavigationBlocked() ? "disabled" : ""}`}
              onClick={(e) => {
                if (isNavigationBlocked()) {
                  e.preventDefault()
                }
              }}
              tabIndex={isNavigationBlocked() ? -1 : 0}
            >
              {t("interface.about", "Over Ons")}
            </a>
            <a
              href="/lessons"
              className={`nav-link ${currentPage === "lessons" ? "active" : ""} ${isNavigationBlocked() ? "disabled" : ""}`}
              onClick={(e) => {
                if (isNavigationBlocked()) {
                  e.preventDefault()
                }
              }}
              tabIndex={isNavigationBlocked() ? -1 : 0}
            >
              {t("interface.lessons", "Lessons")}
            </a>
            {showOntdek && (
              <a
                href="/ontdek"
                className={`nav-link ${currentPage === "ontdek" ? "active" : ""} ${isNavigationBlocked() ? "disabled" : ""}`}
                onClick={(e) => {
                  if (isNavigationBlocked()) {
                    e.preventDefault()
                  }
                }}
                tabIndex={isNavigationBlocked() ? -1 : 0}
              >
                {t("interface.discover", "Ontdek")}
              </a>
            )}
            <a
  href="/teachers"
  className={`nav-link ${currentPage === "teachers" ? "active" : ""} ${isNavigationBlocked() ? "disabled" : ""}`}
  onClick={(e) => {
    if (isNavigationBlocked()) e.preventDefault()
  }}
  tabIndex={isNavigationBlocked() ? -1 : 0}
>
  {t("interface.schools", "Voor Scholen")}
</a>

            <div className="header-auth-group">
              {isSignedIn && userName && (
                <span className="header-welcome" title={userName}>
                  {t("interface.welcome", "Welkom")}, <strong>{userName.trim().split(/\s+/)[0]}</strong>
                </span>
              )}
              {!isSignedIn && (
                <a
                  href="/portal/login.php"
                  className={`login-btn-secondary ${isNavigationBlocked() ? "disabled" : ""}`}
                  onClick={(e) => {
                    if (isNavigationBlocked()) {
                      e.preventDefault()
                    }
                  }}
                  tabIndex={isNavigationBlocked() ? -1 : 0}
                >
                  <User size={15} className="login-icon" />
                  {t("interface.login", "Inloggen")}
                </a>
              )}

              <a
                href={isSignedIn ? "/portal/" : "/portal/register.php"}
                className={`login-button ${isNavigationBlocked() ? "disabled" : ""}`}
                onClick={(e) => {
                  if (isNavigationBlocked()) {
                    e.preventDefault()
                  }
                }}
                tabIndex={isNavigationBlocked() ? -1 : 0}
              >
                {isSignedIn ? (
                  <>
                    <User size={15} className="login-icon" />
                    {t("interface.account", "Account")}
                  </>
                ) : (
                  <>
                    <Sparkles size={15} className="login-icon" />
                    {t("interface.getStarted", "Registreren")}
                  </>
                )}
              </a>
            </div>
          </div>

          <button
            className="mobile-menu-button"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
            disabled={isNavigationBlocked()}
            tabIndex={isNavigationBlocked() ? -1 : 0}
          >
            <Menu size={24} />
          </button>
        </div>
      </header>

      <div className={`nav-overlay ${mobileMenuOpen ? "open" : ""}`} onClick={toggleMobileMenu}>
        <div className={`nav-sidebar ${mobileMenuOpen ? "open" : ""}`} onClick={(e) => e.stopPropagation()}>
          <div className="nav-sidebar-header">
            <h2 className="nav-sidebar-title"></h2>
            <button className="nav-sidebar-close" onClick={toggleMobileMenu} aria-label="Close menu">
              <X size={24} />
            </button>
          </div>
          <div className="nav-sidebar-content">
            <a
              href="/"
              className={`nav-mobile-link ${currentPage === "home" ? "active" : ""} ${isNavigationBlocked() ? "disabled" : ""}`}
              onClick={(e) => {
                if (isNavigationBlocked()) {
                  e.preventDefault()
                } else {
                  handleNavigation("home")
                }
              }}
              tabIndex={isNavigationBlocked() ? -1 : 0}
            >
              <Home size={20} />
              {t("interface.home", "Home")}
            </a>

            <a
              href="/about"
              className={`nav-mobile-link ${currentPage === "about" ? "active" : ""} ${isNavigationBlocked() ? "disabled" : ""}`}
              onClick={(e) => {
                if (isNavigationBlocked()) {
                  e.preventDefault()
                } else {
                  handleNavigation("about")
                }
              }}
              tabIndex={isNavigationBlocked() ? -1 : 0}
            >
              <Home size={20} />
              {t("interface.about", "Over Ons")}
            </a>

            <a
              href="/lessons"
              className={`nav-mobile-link ${currentPage === "lessons" ? "active" : ""} ${isNavigationBlocked() ? "disabled" : ""}`}
              onClick={(e) => {
                if (isNavigationBlocked()) {
                  e.preventDefault()
                } else {
                  handleNavigation("lessons")
                }
              }}
              tabIndex={isNavigationBlocked() ? -1 : 0}
            >
              <Home size={20} />
              {t("interface.lessons", "Lessons")}
            </a>
            {showOntdek && (
              <a
                href="/ontdek"
                className={`nav-mobile-link ${currentPage === "ontdek" ? "active" : ""} ${isNavigationBlocked() ? "disabled" : ""}`}
                onClick={(e) => {
                  if (isNavigationBlocked()) {
                    e.preventDefault()
                  } else {
                    handleNavigation("ontdek")
                  }
                }}
                tabIndex={isNavigationBlocked() ? -1 : 0}
              >
                <Home size={20} />
                {t("interface.discover", "Ontdek")}
              </a>
            )}
            <a
              href="/teachers"
              className={`nav-mobile-link ${currentPage === "teachers" ? "active" : ""} ${isNavigationBlocked() ? "disabled" : ""}`}
              onClick={(e) => {
                if (isNavigationBlocked()) {
                  e.preventDefault()
                } else {
                  handleNavigation("teachers")
                }
              }}
              tabIndex={isNavigationBlocked() ? -1 : 0}
            >
              <Home size={20} />
              {t("interface.schools", "Voor Scholen")}
            </a>

            {isSignedIn && userName && (
              <span className="nav-mobile-link nav-mobile-welcome" title={userName}>
                {t("interface.welcome", "Welkom")}, {userName.trim().split(/\s+/)[0]}
              </span>
            )}
            {!isSignedIn && (
              <a
                href="/portal/login.php"
                className={`nav-mobile-link ${isNavigationBlocked() ? "disabled" : ""}`}
                onClick={(e) => {
                  if (isNavigationBlocked()) {
                    e.preventDefault()
                  } else {
                    setMobileMenuOpen(false)
                  }
                }}
                tabIndex={isNavigationBlocked() ? -1 : 0}
              >
                <User size={20} />
                {t("interface.login", "Inloggen")}
              </a>
            )}

            <a
              href={isSignedIn ? "/portal/" : "/portal/register.php"}
              className={`nav-mobile-link login-button ${isNavigationBlocked() ? "disabled" : ""}`}
              onClick={(e) => {
                if (isNavigationBlocked()) {
                  e.preventDefault()
                } else {
                  setMobileMenuOpen(false)
                }
              }}
              tabIndex={isNavigationBlocked() ? -1 : 0}
            >
              <User size={20} />
              {isSignedIn ? t("interface.account", "Account") : t("interface.getStarted", "Registreren")}
            </a>
          </div>
        </div>
      </div>
    </>
  )
}

export const Footer = ({
  appConfig,
  t,
  currentLessonId,
  isLessonContext,
}: {
  appConfig: any
  t: (path: string, defaultText: string) => string
  currentLessonId?: string
  isLessonContext?: boolean
}) => {
  const { language } = useLanguage()
  const { isLessonUnlocked } = usePasswordCheck()

  const isNavigationBlocked = () => {
    if (!isLessonContext || !currentLessonId) return false
    return !isLessonUnlocked(currentLessonId)
  }

  return (
    <footer className="simple-footer">
      <div className="footer-container">
        <div className="footer-logo">
          {appConfig?.branding?.logo && !appConfig.branding.logo.includes("undefined") ? (
            <img
              src={appConfig.branding.logo || "/placeholder.svg"}
              alt={appConfig?.branding?.logoAlt || "Logo"}
              className="footer-logo-image"
            />
          ) : (
            <div className="footer-text-logo">{appConfig?.branding?.logoAlt || "Techtok"}</div>
          )}
        </div>

        <div className="footer-copyright">
          <p>
            {appConfig?.footer?.copyright?.[language.code]?.replace("{year}", new Date().getFullYear()) ||
              `© ${new Date().getFullYear()} ${appConfig?.footer?.ownerName || "Blinkli"}. All rights reserved.`}
            &nbsp;{t("footer.copyrightText", "Created by")}{" "}
            <a
              href={appConfig?.footer?.learnMoreLink || "https://techtok.me"}
              className={isNavigationBlocked() ? "disabled" : ""}
              onClick={(e) => {
                if (isNavigationBlocked()) {
                  e.preventDefault()
                }
              }}
              tabIndex={isNavigationBlocked() ? -1 : 0}
            >
              {" "}
              {appConfig?.footer?.ownerName || "Techtok Academy"}
            </a>
          </p>
        </div>

        <div className="footer-legal">
          <a href="/portal/privacy-terms.php">
            {language.code === "nl" ? "Privacybeleid & Voorwaarden" : "Privacy Policy & Terms"}
          </a>
        </div>

        <div className="footer-language">
          <LanguageSwitcher />
        </div>
      </div>
    </footer>
  )
}

export default Navigation
